/**
 * FeaturedUnionsStrip — landing-page hook. Fetches the 8 most
 * recognisable unions from ``/api/public/associations/featured``
 * biased by the visitor's geo-region (auto-detected on mount via
 * ``/api/geo/detect``, cached in localStorage for 24 h).
 *
 * Motivates newcomers to click through to the full directory + start
 * their claim in under a minute.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import UnionLogo from "@/components/UnionLogo";
import {
  ShieldCheck, ChevronRight, MapPin, Users, Search, X, Loader2,
} from "lucide-react";

const REGION_LABEL = {
  north: "North India",
  south: "South India",
  east:  "East India",
  west:  "West India",
};

const CACHE_KEY = "cs_geo_region_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

async function getRegion() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.bucket || "";
  } catch { /* corrupt cache — ignore */ }
  try {
    const { data } = await api.get("/geo/detect", { timeout: 3500 });
    const bucket = data.region_bucket || "";
    localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), bucket }));
    return bucket;
  } catch {
    return "";
  }
}

export default function FeaturedUnionsStrip() {
  const [rows, setRows]     = useState([]);
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(true);

  // ── Inline search state ────────────────────────────────────────────
  //   `query`   → what the user is typing (debounced into `debounced`)
  //   `hits`    → live search results (server-side filter). Empty →
  //               fall back to the 8 featured tiles.
  //   `total`   → total registry size (used in the placeholder copy)
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [total, setTotal] = useState(null);
  const searchAbortRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const bucket = await getRegion();
      if (!alive) return;
      setRegion(bucket);
      const params = bucket ? { region: bucket, limit: 8 } : { limit: 8 };
      try {
        const { data } = await api.get("/public/associations/featured", { params });
        if (!alive) return;
        setRows(data.results || []);
      } catch { /* silent — this is a soft nudge; landing must render regardless */ }
      finally { alive && setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Debounce the query — 220ms feels snappy without hammering the API.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 220);
    return () => clearTimeout(t);
  }, [query]);

  // Fire the search whenever the debounced query changes. Cancels the
  // previous inflight request so we never render stale results.
  useEffect(() => {
    if (!debounced) {
      setHits([]); setSearching(false);
      return;
    }
    setSearching(true);
    const ctrl = new AbortController();
    searchAbortRef.current?.abort();
    searchAbortRef.current = ctrl;
    api.get("/public/associations", {
      params: { q: debounced, sort: "popular" },
      signal: ctrl.signal,
    })
      .then(({ data }) => {
        setHits((data.results || []).slice(0, 8));
        if (data.total != null) setTotal(data.total);
      })
      .catch((err) => {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          setHits([]);
        }
      })
      .finally(() => setSearching(false));
    return () => ctrl.abort();
  }, [debounced]);

  // What the grid actually renders — search hits when the user is
  // typing, otherwise the geo-biased featured 8.
  const activeSearch = debounced.length > 0;
  const gridRows = activeSearch ? hits : rows;
  const registrySize = useMemo(
    () => total ?? (rows.length > 8 ? rows.length : 220),
    [total, rows.length],
  );

  if (loading || !rows.length) return null;

  return (
    <section
      className="border-b border-white/10 bg-[#0a0a0a] py-16 lg:py-20"
      data-testid="featured-unions-strip"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck size={14} className="text-[#F5B82E]" />
              <span className="text-[11px] tracking-[0.32em] uppercase text-[#F5B82E]">
                {region ? `Trusted in ${REGION_LABEL[region]}` : "Verified · Industry Union Registry"}
              </span>
            </div>
            <h3 className="font-display text-3xl sm:text-4xl uppercase tracking-tight leading-[0.95]"
                data-testid="featured-unions-title">
              Your union is<br />already on Call Sheet
            </h3>
          </div>

          <Link
            to="/associations"
            className="group inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/25 text-[11px] uppercase tracking-[0.24em] font-semibold text-white/90 hover:border-[#F5B82E] hover:text-[#F5B82E] transition-colors"
            data-testid="featured-unions-see-all">
            See the full registry
            <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        {/* Inline search — filters the registry live without leaving the
            landing page. Empty query keeps the 8 featured tiles as-is. */}
        <div className="mb-6"
             data-testid="featured-unions-search-wrap">
          <label htmlFor="featured-unions-search"
                 className="sr-only">Search unions & guilds</label>
          <div className="relative max-w-xl">
            <Search size={15}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              id="featured-unions-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${registrySize}+ unions · e.g. FWICE, CINTAA, SIFAA, IPRS`}
              autoComplete="off"
              className="w-full h-12 pl-11 pr-11 rounded-full bg-black/50 border border-white/15 focus:border-[#F5B82E]/70 focus:outline-none text-[13px] text-white placeholder:text-white/35 tracking-[0.02em] transition-colors"
              data-testid="featured-unions-search-input" />
            {searching && (
              <Loader2 size={14}
                       className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 animate-spin"
                       data-testid="featured-unions-search-spinner" />
            )}
            {!searching && query && (
              <button type="button"
                      onClick={() => setQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full inline-flex items-center justify-center text-white/45 hover:text-white hover:bg-white/10 transition-colors"
                      aria-label="Clear search"
                      data-testid="featured-unions-search-clear">
                <X size={14} />
              </button>
            )}
          </div>
          {activeSearch && (
            <div className="mt-3 text-[11px] tracking-[0.2em] uppercase text-white/45"
                 data-testid="featured-unions-search-status">
              {searching
                ? "Searching…"
                : hits.length
                    ? `Showing ${hits.length} match${hits.length === 1 ? "" : "es"} for “${debounced}”`
                    : `No unions match “${debounced}” — try a shorter term`}
              {!!hits.length && (
                <Link to={`/associations?q=${encodeURIComponent(debounced)}`}
                      className="ml-3 text-[#F5B82E] hover:underline normal-case tracking-normal"
                      data-testid="featured-unions-search-openall">
                  Open full results →
                </Link>
              )}
            </div>
          )}
        </div>

        {gridRows.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4"
               data-testid={activeSearch ? "featured-unions-search-grid" : "featured-unions-grid"}>
            {gridRows.map((r) => <UnionTile key={r.id} r={r} />)}
          </div>
        ) : activeSearch && !searching ? (
          <div className="rounded-xl border border-dashed border-white/12 bg-black/30 p-6 text-center"
               data-testid="featured-unions-search-empty">
            <div className="font-display text-lg uppercase tracking-tight text-white/70">
              Can&apos;t find your union?
            </div>
            <p className="mt-2 text-[13px] text-white/50 max-w-md mx-auto">
              We&apos;re adding new bodies every week. If yours isn&apos;t listed,
              <Link to="/associations" className="text-[#F5B82E] hover:underline"> browse the full directory</Link>
              {" "}or write to us and we&apos;ll add it in 48h.
            </p>
          </div>
        ) : null}

        <p className="mt-8 text-[13px] text-white/50 max-w-2xl">
          {rows.length}+ unions and guilds — from FWICE and CINTAA to FEFSI and IPRS —
          all searchable in one place. Claim your digital membership card in under a minute.
        </p>
      </div>
    </section>
  );
}

function UnionTile({ r }) {
  const kMembers = r.member_count >= 1000
    ? `${(r.member_count / 1000).toFixed(1)}k`
    : (r.member_count || null);
  return (
    <Link
      to={`/associations/${r.id}`}
      className="group relative rounded-xl p-4 sm:p-5 border border-white/8 bg-black/40 hover:bg-black/60 hover:border-[#F5B82E]/60 transition-colors block overflow-hidden"
      data-testid={`featured-union-${r.id}`}
      style={{ borderLeftColor: r.color, borderLeftWidth: 3 }}
    >
      <div className="flex items-start gap-3">
        <UnionLogo assoc={r} size={44} rounded="lg" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg sm:text-xl uppercase tracking-tight truncate"
            style={{ color: r.color }}>
            {r.name}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-white/50 truncate">
            {r.full}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px] text-white/45">
        {r.city && <span className="inline-flex items-center gap-1"><MapPin size={9}/> {r.city}</span>}
        {kMembers && <span className="inline-flex items-center gap-1"><Users size={9}/> {kMembers} members</span>}
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-white/0 group-hover:text-[#F5B82E] transition-colors">
        Claim your card →
      </div>
    </Link>
  );
}
